import { useMemo } from "react";

import {
  ObservationsMapWidgetConfiguration,
  ParametersObservedParameter,
  ParameterTimeSeriesConfiguration,
  readObservationsMapTimeSeriesConfigurations,
} from "@/lib/api";
import type { ObservationsMapParameterDisplayGroup } from "@/lib/api";

import type { ConfiguredMapLayer } from "./build-observations-map-layer-configs";
import {
  useObservedMapLayersQuery,
  useObservedParametersForConfigurations,
} from "./hooks/queries";

/** Catalog-resolved view of an observations-map configuration. */
export type ObservationsMapResolvedConfig = {
  /** Observed map (tile) layers joined with their widget config, in config order. */
  configuredLayers: ConfiguredMapLayer[];
  /** Observed-parameter catalog entries referenced by the time-series config. */
  configuredParameters: ParametersObservedParameter[];
  timeSeriesConfigurations: ParameterTimeSeriesConfiguration[];
  displayGroups: ObservationsMapParameterDisplayGroup[];
  /** Whether the widget has any station time-series (drives the marker layer). */
  hasStationLayer: boolean;
  /** Whether at least one observed tile layer resolved against the catalog. */
  hasObservedLayers: boolean;
  /** Whether the widget declares any static overlays. */
  hasStaticLayers: boolean;
  /** Whether the widget config asked for parameter (tile) layers at all. */
  wantsParameterLayers: boolean;
  /** Whether the observed-layers catalog query is still resolving. */
  layersStillResolving: boolean;
};

/**
 * Resolve an observations-map configuration against the observed-layers and
 * observed-parameters catalogs, joining widget config to catalog entries.
 *
 * Container-agnostic: both the dashboard smart container and the CMS preview
 * use it. It fires the same catalog queries in either context.
 */
export function useObservationsMapResolvedConfig(
  configuration: ObservationsMapWidgetConfiguration,
): ObservationsMapResolvedConfig {
  const timeSeriesConfigurations =
    readObservationsMapTimeSeriesConfigurations(configuration);
  const displayGroups = configuration.parameter_display_groups ?? [];
  const hasStationLayer = timeSeriesConfigurations.length > 0;

  const layersData = useObservedMapLayersQuery();

  const parametersQuery = useObservedParametersForConfigurations(
    timeSeriesConfigurations,
    hasStationLayer,
  );

  const configuredLayers = useMemo(() => {
    if (!layersData.data) return [];
    return (configuration.parameter_map_layer_configurations ?? [])
      .map((widgetConfig) => {
        const layer = layersData.data.find(
          (it) => it.id === widgetConfig.parameter_map_layer_id,
        );
        return layer ? { layer, widgetConfig } : null;
      })
      .filter((item): item is ConfiguredMapLayer => !!item);
  }, [layersData.data, configuration]);

  const wantsParameterLayers =
    (configuration.parameter_map_layer_configurations?.length ?? 0) > 0;
  const layersStillResolving = layersData.isPending || layersData.isFetching;
  const hasObservedLayers = configuredLayers.length > 0;
  const hasStaticLayers =
    (configuration.static_map_overlay_configurations?.length ?? 0) > 0;

  return {
    configuredLayers,
    configuredParameters: parametersQuery.data ?? [],
    timeSeriesConfigurations,
    displayGroups,
    hasStationLayer,
    hasObservedLayers,
    hasStaticLayers,
    wantsParameterLayers,
    layersStillResolving,
  };
}
